import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2 } from "lucide-react";

import { QuickBooksBillTextLink } from "@/components/hub/QuickBooksTxnLink";
import { MegaventoryPurchaseInvoiceExternalLink } from "@/components/mrp/MegaventoryPurchaseOrderLinks";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { CopyToClipboardGlyph } from "@/components/ui/CopyToClipboardGlyph";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import {
  fetchApInvoiceStandaloneTshSourceCandidates,
  fetchApInvoiceTshHandoff,
  fetchApInvoiceTshParentCandidates,
  linkApInvoiceTshPurchaseInvoice,
  openApInvoiceTshMegaventory,
  createApInvoicePriorMonthGipi,
  patchApInvoice,
  setApInvoiceTshParent,
  type ApInvoiceRow,
  type ApInvoiceStandaloneTshSourceCandidate,
  type ApInvoiceTshMatchCandidate,
} from "./apInvoicesApi";
import {
  apInvoiceMegaventoryTshReference,
  apInvoiceStandaloneGipiSearchReady,
  apInvoiceTshAmountClipboardText,
  apInvoiceTshWorkflowBadgeTone,
  apInvoiceUsesStandaloneGipiSearch,
  formatApInvoiceLinkedGipiMvNumber,
  formatApInvoiceTshAmountLabel,
  formatApInvoiceTshParentCandidateLabel,
  parseApInvoiceTshAmount,
} from "./apInvoiceTshUi";

const AP_INVOICES_LIST_KEY = ["accounting", "ap-invoices"] as const;
const GIPI_PARENT_SEARCH_DEBOUNCE_MS = 150;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

function formatTimestamp(raw: string | null | undefined): string {
  if (raw == null || raw.trim() === "") return "";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString();
}

function TshClipboardValue(props: { label: string; display: string; copyText: string }): JSX.Element {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{props.label}</p>
      <p className="inline-flex items-center gap-1 text-sm text-slate-900">
        <span>{props.display}</span>
        <CopyToClipboardGlyph text={props.copyText} label={props.label.toLowerCase()} />
      </p>
    </div>
  );
}

function formatMoney(amount: string | null | undefined, currency: string): string {
  if (amount == null || amount.trim() === "") return "—";
  const parsed = Number.parseFloat(amount);
  if (!Number.isFinite(parsed)) return amount;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.trim() !== "" ? currency : "USD",
      maximumFractionDigits: 2,
    }).format(parsed);
  } catch {
    return `${parsed.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency}`;
  }
}

export function useApInvoiceTshOpenMegaventory({
  invoiceId,
  invoice,
  onUpdated,
  onOpened,
}: {
  invoiceId: string;
  invoice: ApInvoiceRow;
  onUpdated: (invoice: ApInvoiceRow) => void;
  onOpened?: () => void;
}) {
  const queryClient = useQueryClient();

  const handoffQuery = useQuery({
    queryKey: ["accounting", "ap-invoice", invoiceId, "tsh-handoff"],
    queryFn: () => fetchApInvoiceTshHandoff(invoiceId),
    enabled: invoice.status === "pending_tsh",
    refetchInterval: (query) => {
      const status = query.state.data?.workflow_status ?? invoice.tsh_workflow_status;
      if (status === "linked" || status === "failed") return false;
      return 15_000;
    },
    staleTime: 5_000,
  });

  const openMegaventoryMutation = useMutation({
    mutationFn: (purchaseInvoiceId?: number) => openApInvoiceTshMegaventory(invoiceId, purchaseInvoiceId),
    onSuccess: ({ invoice: updated, handoff }) => {
      onUpdated(updated);
      queryClient.setQueryData(["accounting", "ap-invoice", invoiceId, "tsh-handoff"], handoff);
      if (handoff.megaventory_url != null) {
        window.open(handoff.megaventory_url, "_blank", "noopener,noreferrer");
      }
      onOpened?.();
    },
  });

  const workflowStatus = handoffQuery.data?.workflow_status ?? invoice.tsh_workflow_status;
  const megaventoryTshReference = apInvoiceMegaventoryTshReference(invoice.vendor_invoice_number);
  const canOpenMegaventoryBase =
    invoice.tsh_workflow?.can_open_megaventory === true ||
    workflowStatus === "awaiting_ingest" ||
    workflowStatus === "failed";
  const createPath = handoffQuery.data?.tsh_create_path ?? null;
  const priorMonthGipi = createPath === "prior_month_gipi";
  const canOpenMegaventory = canOpenMegaventoryBase && megaventoryTshReference !== "" && !priorMonthGipi;
  const megaventoryButtonLabel =
    workflowStatus === "awaiting_ingest" || workflowStatus === "failed"
      ? "Open Megaventory again"
      : "Create TSH in Megaventory";

  const createGipiMutation = useMutation({
    mutationFn: () => createApInvoicePriorMonthGipi(invoiceId),
    onSuccess: ({ invoice: updated, handoff }) => {
      onUpdated(updated);
      queryClient.setQueryData(["accounting", "ap-invoice", invoiceId, "tsh-handoff"], handoff);
      onOpened?.();
    },
  });

  return {
    canOpenMegaventory,
    megaventoryButtonLabel,
    openMegaventoryMutation,
    createGipiMutation,
    priorMonthGipi,
    workflowStatus,
    gipiTargets: (handoffQuery.data?.gipi_targets ?? []).filter((target) => target.megaventory_url != null),
  };
}

export function ApInvoiceTshOpenMegaventoryButton({
  invoiceId,
  invoice,
  onUpdated,
  onOpened,
}: {
  invoiceId: string;
  invoice: ApInvoiceRow;
  onUpdated: (invoice: ApInvoiceRow) => void;
  onOpened?: () => void;
}) {
  const {
    canOpenMegaventory,
    megaventoryButtonLabel,
    openMegaventoryMutation,
    createGipiMutation,
    priorMonthGipi,
    workflowStatus,
    gipiTargets,
  } = useApInvoiceTshOpenMegaventory({
    invoiceId,
    invoice,
    onUpdated,
    onOpened,
  });

  if (priorMonthGipi) {
    const alreadyCreated = invoice.tsh_workflow_status === "linked" || invoice.tsh_link_error?.startsWith("prior_month_gipi_mv:") === true;
    return (
      <div className="flex max-w-md flex-col items-end gap-1">
        {alreadyCreated ? null : (
          <Button
            type="button"
            onClick={() => createGipiMutation.mutate()}
            disabled={createGipiMutation.isPending || workflowStatus === "awaiting_ingest"}
          >
            {createGipiMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                Creating GIPI…
              </>
            ) : (
              "Create GIPI"
            )}
          </Button>
        )}
        {createGipiMutation.isError ? (
          <p className="text-right text-sm text-rose-700">
            {createGipiMutation.error instanceof Error
              ? createGipiMutation.error.message
              : "The replacement GIPI could not be created."}
          </p>
        ) : null}
      </div>
    );
  }

  if (!canOpenMegaventory && !openMegaventoryMutation.isError) {
    return null;
  }

  if (gipiTargets.length > 1) {
    return (
      <div className="flex max-w-md flex-col items-end gap-2">
        {gipiTargets.map((target) => (
          <Button
            key={target.purchase_invoice_id}
            type="button"
            onClick={() => openMegaventoryMutation.mutate(target.purchase_invoice_id)}
            disabled={openMegaventoryMutation.isPending}
          >
            {`Create TSH for ${target.mv_number}`}
            <ExternalLink className="ml-2 h-4 w-4 shrink-0" aria-hidden />
          </Button>
        ))}
        {openMegaventoryMutation.isError ? (
          <p role="alert" className="text-right text-sm text-rose-700">
            {openMegaventoryMutation.error instanceof Error
              ? openMegaventoryMutation.error.message
              : "Could not open Megaventory."}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex max-w-md flex-col items-end gap-1">
      {canOpenMegaventory ? (
        <Button
          type="button"
          onClick={() => openMegaventoryMutation.mutate(undefined)}
          disabled={openMegaventoryMutation.isPending}
        >
          {openMegaventoryMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
              Opening Megaventory…
            </>
          ) : (
            <>
              {megaventoryButtonLabel}
              <ExternalLink className="ml-2 h-4 w-4 shrink-0" aria-hidden />
            </>
          )}
        </Button>
      ) : null}
      {openMegaventoryMutation.isError ? (
        <p role="alert" className="text-right text-sm text-rose-700">
          {openMegaventoryMutation.error instanceof Error
            ? openMegaventoryMutation.error.message
            : "Could not open Megaventory."}
        </p>
      ) : null}
    </div>
  );
}

function candidateReviewLabel(candidate: ApInvoiceTshMatchCandidate): string {
  const flags: string[] = [];
  if (candidate.reference_matches) flags.push("reference");
  if (candidate.currency_matches) flags.push("currency");
  if (candidate.amount_matches) flags.push("amount");
  if (candidate.supplier_matches) flags.push("supplier");
  if (flags.length === 0) return "Review required";
  return `Matches: ${flags.join(", ")}`;
}

function formatStandaloneTshSourceCandidateLabel(
  candidate: ApInvoiceStandaloneTshSourceCandidate,
): string {
  const parts: string[] = [];
  if (candidate.vendor_invoice_number.trim() !== "") {
    parts.push(candidate.vendor_invoice_number.trim());
  }
  if (candidate.purchase_order_number != null && candidate.purchase_order_number.trim() !== "") {
    parts.push(`PO ${candidate.purchase_order_number.trim()}`);
  }
  if (
    candidate.purchase_invoice_mv_number != null &&
    candidate.purchase_invoice_mv_number.trim() !== ""
  ) {
    parts.push(candidate.purchase_invoice_mv_number.trim());
  }
  if (candidate.stock_supplier_name != null && candidate.stock_supplier_name.trim() !== "") {
    parts.push(candidate.stock_supplier_name.trim());
  }
  return parts.length > 0 ? parts.join(" · ") : `AP #${candidate.id}`;
}

export function ApInvoiceStandaloneTshSourceSelector({
  invoiceId,
  invoice,
  disabled,
  compact = false,
  onUpdated,
}: {
  invoiceId: string;
  invoice: ApInvoiceRow;
  disabled?: boolean;
  /** Render only the picker; the caller supplies the field label. */
  compact?: boolean;
  onUpdated: (invoice: ApInvoiceRow) => void;
}) {
  const [sourceSearch, setSourceSearch] = useState("");
  const sourceSearchDebounced = useDebouncedValue(sourceSearch.trim(), GIPI_PARENT_SEARCH_DEBOUNCE_MS);
  const selectedSourceId = invoice.standalone_tsh_source_accounts_payable_invoice_id ?? "";

  const candidatesQuery = useQuery({
    queryKey: [
      "accounting",
      "ap-invoice",
      invoiceId,
      "standalone-tsh-source-candidates",
      sourceSearchDebounced,
    ],
    queryFn: () => fetchApInvoiceStandaloneTshSourceCandidates(invoiceId, sourceSearchDebounced),
    enabled: invoice.purchase_kind === "standalone_tsh",
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const setSourceMutation = useMutation({
    mutationFn: (sourceAccountsPayableInvoiceId: number) =>
      patchApInvoice(invoiceId, {
        standalone_tsh_source_accounts_payable_invoice_id: sourceAccountsPayableInvoiceId,
      }),
    onSuccess: (updated) => onUpdated(updated),
  });

  const options = useMemo(() => {
    return (candidatesQuery.data ?? []).map((candidate) => ({
      value: candidate.id,
      label: formatStandaloneTshSourceCandidateLabel(candidate),
    }));
  }, [candidatesQuery.data]);

  const selectedSourceLabel = useMemo(() => {
    if (selectedSourceId === "") return null;
    const fromCandidates = (candidatesQuery.data ?? []).find((candidate) => candidate.id === selectedSourceId);
    if (fromCandidates != null) return formatStandaloneTshSourceCandidateLabel(fromCandidates);
    const linkedNumber = invoice.standalone_tsh_source_vendor_invoice_number?.trim() ?? "";
    return linkedNumber !== "" ? linkedNumber : `Vendor invoice #${selectedSourceId}`;
  }, [candidatesQuery.data, invoice.standalone_tsh_source_vendor_invoice_number, selectedSourceId]);

  if (invoice.purchase_kind !== "standalone_tsh") {
    return null;
  }

  return (
    <div className="space-y-2">
      {compact ? null : (
        <>
          <div className="flex items-center gap-1">
            <Label htmlFor="ap-standalone-tsh-source">Stock vendor invoice</Label>
            <HoverHintWithGlyph hoverHint="Search by purchase order number or the original vendor invoice number. The number on this TSH invoice is a different document." />
          </div>
        </>
      )}
      <SearchableSelect
        id="ap-standalone-tsh-source"
        className="w-full"
        presentation="inlineFilter"
        filterLocally={false}
        seedCommittedLabelOnFocus={false}
        value={selectedSourceId}
        selectedLabelOverride={selectedSourceLabel}
        onValueChange={(option) => {
          if (option == null) return;
          const parsed = Number.parseInt(option.value, 10);
          if (Number.isFinite(parsed) && parsed > 0) {
            setSourceSearch("");
            setSourceMutation.mutate(parsed);
          }
        }}
        options={options}
        searchValue={sourceSearch}
        onSearchChange={(next) => {
          if (selectedSourceLabel != null && next.trim() === selectedSourceLabel.trim()) {
            setSourceSearch("");
            return;
          }
          setSourceSearch(next);
        }}
        placeholder="Search purchase order or vendor invoice number"
        searchPlaceholder="Search purchase order or vendor invoice number"
        emptyMessage={
          candidatesQuery.isFetching
            ? "Loading…"
            : sourceSearchDebounced.trim() === ""
              ? "Type a purchase order number or the original vendor invoice number. This TSH invoice number is a different document."
              : "No purchase order vendor invoices match that search."
        }
        isLoading={candidatesQuery.isFetching && (candidatesQuery.data ?? []).length === 0}
        disabled={disabled || setSourceMutation.isPending}
      />
      {setSourceMutation.isError ? (
        <p role="alert" className="text-sm text-rose-700">
          {setSourceMutation.error instanceof Error
            ? setSourceMutation.error.message
            : "Could not save stock vendor invoice link."}
        </p>
      ) : null}
    </div>
  );
}

export function ApInvoiceTshParentSelector({
  invoiceId,
  invoice,
  disabled,
  standaloneDraft = false,
  onUpdated,
}: {
  invoiceId: string;
  invoice: ApInvoiceRow;
  disabled?: boolean;
  standaloneDraft?: boolean;
  onUpdated: (invoice: ApInvoiceRow) => void;
}) {
  const [parentSearch, setParentSearch] = useState("");
  const [purchaseOrderFilter, setPurchaseOrderFilter] = useState("");
  const [pickedParentId, setPickedParentId] = useState<string | null>(null);
  const [pickedParentLabel, setPickedParentLabel] = useState<string | null>(null);
  const standaloneGipiPick = apInvoiceUsesStandaloneGipiSearch({
    standaloneDraft,
    purchaseKind: invoice.purchase_kind,
  });
  const standaloneSearchReady = apInvoiceStandaloneGipiSearchReady({
    standaloneDraft,
    purchaseKind: invoice.purchase_kind,
  });
  const parentSearchDebounced = useDebouncedValue(parentSearch.trim(), GIPI_PARENT_SEARCH_DEBOUNCE_MS);
  const purchaseOrderDebounced = useDebouncedValue(purchaseOrderFilter.trim(), GIPI_PARENT_SEARCH_DEBOUNCE_MS);
  const purchaseOrderLock = standaloneGipiPick && purchaseOrderDebounced.length >= 2;

  const selectedParentId = pickedParentId ?? invoice.tsh_parent_purchase_invoice_id ?? "";
  const needsParentSelection =
    standaloneDraft ||
    invoice.tsh_workflow?.can_choose_parent === true ||
    (invoice.tsh_parent_purchase_invoice_id == null &&
      invoice.status !== "submitted" &&
      invoice.tsh_workflow?.shape === "standalone" &&
      parseApInvoiceTshAmount(invoice.tsh_amount) != null);

  if (!needsParentSelection && selectedParentId === "") {
    return null;
  }

  const candidatesQuery = useQuery({
    queryKey: [
      "accounting",
      "ap-invoice",
      invoiceId,
      "tsh-parent-candidates",
      purchaseOrderLock ? purchaseOrderDebounced : standaloneGipiPick ? parentSearchDebounced : "",
      invoice.purchase_kind ?? "",
    ],
    queryFn: () =>
      fetchApInvoiceTshParentCandidates(
        invoiceId,
        purchaseOrderLock || !standaloneGipiPick ? null : parentSearchDebounced,
        purchaseOrderLock ? purchaseOrderDebounced : null,
      ),
    enabled: needsParentSelection && standaloneSearchReady && (purchaseOrderLock || parentSearchDebounced.length >= 2),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    placeholderData: standaloneGipiPick ? keepPreviousData : undefined,
  });

  const setParentMutation = useMutation({
    mutationFn: (parentPurchaseInvoiceId: number | null) =>
      setApInvoiceTshParent(invoiceId, parentPurchaseInvoiceId),
    onMutate: (parentPurchaseInvoiceId) => {
      if (parentPurchaseInvoiceId == null) {
        onUpdated({
          ...invoice,
          tsh_parent_purchase_invoice_id: null,
          tsh_parent_mv_number: null,
          tsh_workflow_status:
            invoice.purchase_kind === "standalone_tsh" ? "waiting_for_gipi" : invoice.tsh_workflow_status,
        });
      }
      return { previous: invoice };
    },
    onError: (_error, _parent, context) => {
      setPickedParentId(null);
      setPickedParentLabel(null);
      if (context?.previous) onUpdated(context.previous);
    },
    onSuccess: (updated) => {
      onUpdated({
        ...invoice,
        tsh_parent_purchase_invoice_id:
          updated.tsh_parent_purchase_invoice_id ?? invoice.tsh_parent_purchase_invoice_id,
        tsh_parent_mv_number: updated.tsh_parent_mv_number ?? invoice.tsh_parent_mv_number,
        tsh_workflow_status: updated.tsh_workflow_status ?? invoice.tsh_workflow_status,
      });
    },
  });

  const selectedParentLabel = useMemo(() => {
    if (selectedParentId === "") {
      return null;
    }
    if (pickedParentLabel != null && pickedParentLabel.trim() !== "") {
      return pickedParentLabel;
    }
    const fromCandidates = (candidatesQuery.data?.candidates ?? []).find(
      (candidate) => String(candidate.purchase_invoice_id) === selectedParentId,
    );
    if (fromCandidates != null) {
      return formatApInvoiceTshParentCandidateLabel(fromCandidates);
    }
    if (invoice.tsh_parent_mv_number != null && invoice.tsh_parent_mv_number.trim() !== "") {
      return formatApInvoiceLinkedGipiMvNumber(invoice.tsh_parent_mv_number);
    }
    return null;
  }, [candidatesQuery.data?.candidates, invoice.tsh_parent_mv_number, pickedParentLabel, selectedParentId]);

  const options = useMemo(() => {
    const mapped = (candidatesQuery.data?.candidates ?? []).map((candidate) => ({
      value: String(candidate.purchase_invoice_id),
      label: formatApInvoiceTshParentCandidateLabel(candidate),
    }));
    if (selectedParentId === "" || mapped.some((option) => option.value === selectedParentId)) {
      return mapped;
    }
    const label = selectedParentLabel ?? `GIPI #${selectedParentId}`;
    return [{ value: selectedParentId, label }, ...mapped];
  }, [candidatesQuery.data?.candidates, selectedParentId, selectedParentLabel]);

  const gipiFieldLabel = standaloneGipiPick ? "GIPI document" : "Parent GIPI for TSH";
  const waitingForStandaloneSave =
    standaloneGipiPick && !standaloneSearchReady && parentSearch.trim() !== "";
  const gipiPickerLoading =
    waitingForStandaloneSave ||
    (candidatesQuery.isFetching && (candidatesQuery.data?.candidates ?? []).length === 0);
  const showStandaloneGipiSearch = standaloneGipiPick && (standaloneDraft || needsParentSelection);

  return (
    <div className="space-y-2">
      {standaloneGipiPick ? (
        <div className="space-y-1">
          <Label htmlFor="ap-tsh-purchase-order-filter">Purchase order</Label>
          <Input
            id="ap-tsh-purchase-order-filter"
            value={purchaseOrderFilter}
            disabled={disabled}
            placeholder="GPO number, for example GPO2032"
            autoComplete="off"
            onChange={(event) => setPurchaseOrderFilter(event.target.value)}
          />
          <p className="text-sm text-slate-600">
            {purchaseOrderDebounced.length < 2
              ? "Optional. Shows how many GIPI documents are on that purchase order. It does not select a GIPI."
              : candidatesQuery.isFetching
                ? "Checking GIPI documents…"
                : (candidatesQuery.data?.gipiCount ?? 0) === 0
                  ? "No GIPI documents on this purchase order yet. The GIPI field stays empty until you choose one."
                  : (candidatesQuery.data?.gipiCount ?? 0) === 1
                    ? "1 GIPI document on this purchase order. Choose it only when you are ready."
                    : `${candidatesQuery.data?.gipiCount ?? 0} GIPI documents on this purchase order. Choose one only when you are ready.`}
          </p>
        </div>
      ) : null}
      {!standaloneDraft ? <Label htmlFor="ap-tsh-parent-gipi">{gipiFieldLabel}</Label> : null}
      {candidatesQuery.isError ? (
        <p role="alert" className="text-sm text-rose-700">
          {candidatesQuery.error instanceof Error
            ? candidatesQuery.error.message
            : "Could not load GIPI suggestions."}
        </p>
      ) : showStandaloneGipiSearch || options.length > 0 || selectedParentId !== "" ? (
        <SearchableSelect
          id="ap-tsh-parent-gipi"
          className="w-full"
          presentation={standaloneGipiPick ? "inlineFilter" : "panelSearch"}
          filterLocally={!standaloneGipiPick || purchaseOrderLock}
          seedCommittedLabelOnFocus={!standaloneGipiPick}
          value={selectedParentId}
          selectedLabelOverride={selectedParentLabel}
          onValueChange={(option) => {
            if (option == null) {
              setPickedParentId(null);
              setPickedParentLabel(null);
              setParentSearch("");
              if ((invoice.tsh_parent_purchase_invoice_id ?? "") !== "") {
                setParentMutation.mutate(null);
              }
              return;
            }
            const parsed = Number.parseInt(option.value, 10);
            if (Number.isFinite(parsed) && parsed > 0) {
              setPickedParentId(option.value);
              setPickedParentLabel(option.label);
              setParentSearch("");
              setParentMutation.mutate(parsed);
            }
          }}
          options={options}
          searchValue={parentSearch}
          onSearchChange={setParentSearch}
          placeholder={standaloneGipiPick ? "Search GIPI number, PO, or stock vendor…" : "Select parent GIPI"}
          searchPlaceholder="Type to search…"
          emptyMessage={
            gipiPickerLoading
              ? "Loading…"
              : purchaseOrderLock && (candidatesQuery.data?.gipiCount ?? 0) === 0
                ? "No GIPI documents on this purchase order yet."
                : standaloneGipiPick && !purchaseOrderLock && parentSearchDebounced.trim() === ""
                ? "Type a GIPI number, purchase order, or stock vendor (for example GIPI 3582)."
                : standaloneGipiPick
                  ? "No matching GIPI documents."
                  : "No GIPI documents match."
          }
          isLoading={gipiPickerLoading}
          disabled={disabled}
          clearable
          clearLabel="Clear GIPI"
        />
      ) : (
        <p className="text-sm text-slate-600">No GIPI documents found on this purchase order yet.</p>
      )}
      {setParentMutation.isError ? (
        <p role="alert" className="text-sm text-rose-700">
          {setParentMutation.error instanceof Error
            ? setParentMutation.error.message
            : "Could not save parent GIPI."}
        </p>
      ) : null}
    </div>
  );
}

export function ApInvoiceTshWorkflowPanel({
  invoiceId,
  invoice,
  onUpdated,
}: {
  invoiceId: string;
  invoice: ApInvoiceRow;
  onUpdated: (invoice: ApInvoiceRow) => void;
}) {
  const queryClient = useQueryClient();
  const [selectedCandidateId, setSelectedCandidateId] = useState<string>("");
  const [liveStatusMessage, setLiveStatusMessage] = useState("");

  const handoffQuery = useQuery({
    queryKey: ["accounting", "ap-invoice", invoiceId, "tsh-handoff"],
    queryFn: () => fetchApInvoiceTshHandoff(invoiceId),
    enabled: invoice.status === "pending_tsh",
    refetchInterval: (query) => {
      const status = query.state.data?.workflow_status ?? invoice.tsh_workflow_status;
      if (status === "linked" || status === "failed") return false;
      return 15_000;
    },
    staleTime: 5_000,
  });

  const linkMutation = useMutation({
    mutationFn: (tshPurchaseInvoiceId: number) => linkApInvoiceTshPurchaseInvoice(invoiceId, tshPurchaseInvoiceId),
    onSuccess: ({ invoice: updated }) => {
      onUpdated(updated);
      void queryClient.invalidateQueries({ queryKey: AP_INVOICES_LIST_KEY });
      setLiveStatusMessage("TSH linked. Vendor invoice is complete.");
    },
  });

  const workflowStatus = handoffQuery.data?.workflow_status ?? invoice.tsh_workflow_status;
  const workflowLabel = invoice.tsh_workflow_status_label ?? workflowStatus ?? "Pending TSH";

  useEffect(() => {
    if (workflowStatus === "ready_for_human") {
      setLiveStatusMessage("");
    } else if (workflowStatus === "waiting_for_gipi") {
      setLiveStatusMessage(
        invoice.purchase_kind === "standalone_tsh"
          ? "Choose the GIPI this bill belongs to."
          : "Waiting for a matched purchase invoice (GIPI). The hub links one when receipt and vendor invoice quantities align; it is not created from this page.",
      );
    } else if (workflowStatus === "awaiting_ingest") {
      setLiveStatusMessage("Waiting for saved TSH.");
    } else if (workflowStatus === "needs_link_review") {
      setLiveStatusMessage("Review ingested TSH candidates and confirm the correct document.");
    }
  }, [invoice.purchase_kind, workflowStatus]);

  const reviewCandidates = useMemo(
    () =>
      (handoffQuery.data?.candidate_tsh_documents ?? []).filter(
        (candidate) => candidate.classification !== "reject",
      ),
    [handoffQuery.data?.candidate_tsh_documents],
  );

  const handoff = handoffQuery.data;
  const megaventoryTshReference = apInvoiceMegaventoryTshReference(
    handoff?.vendor_invoice_reference ?? invoice.vendor_invoice_number,
  );
  const tshCurrency = handoff?.currency ?? invoice.currency;
  const tariffCopy = apInvoiceTshAmountClipboardText(handoff?.tariff_amount ?? invoice.tsh_tariff_amount);
  const shippingCopy = apInvoiceTshAmountClipboardText(handoff?.shipping_amount ?? invoice.tsh_shipping_amount);
  const oneOffCopy = apInvoiceTshAmountClipboardText(handoff?.one_off_amount ?? invoice.tsh_one_off_amount);
  const totalCopy = apInvoiceTshAmountClipboardText(handoff?.total_tsh_amount ?? invoice.tsh_amount);
  const totalDisplay = formatMoney(handoff?.total_tsh_amount ?? invoice.tsh_amount, tshCurrency);
  const oneOffDescription = (invoice.tsh_fee_description ?? "").trim();
  const canOpenMegaventoryBase =
    invoice.tsh_workflow?.can_open_megaventory === true ||
    workflowStatus === "awaiting_ingest" ||
    workflowStatus === "failed";

  if (invoice.status !== "pending_tsh" && invoice.tsh_workflow?.is_complete) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>TSH workflow</CardTitle>
        </CardHeader>
        <CardBody className="space-y-2 text-sm text-slate-700">
          {invoice.tsh_parent_mv_number != null ? (
            <p>
              Parent GIPI: <span className="font-medium">{invoice.tsh_parent_mv_number}</span>
            </p>
          ) : null}
          {invoice.tsh_purchase_invoice_mv_number != null ? (
            <p>
              Linked TSH: <span className="font-medium">{invoice.tsh_purchase_invoice_mv_number}</span>{" "}
              <QuickBooksBillTextLink
                txnId={invoice.tsh_purchase_invoice_qb_id}
                documentLabel={invoice.tsh_purchase_invoice_mv_number}
              />
            </p>
          ) : null}
          {invoice.tsh_linked_at != null ? (
            <p className="text-slate-500">Linked {formatTimestamp(invoice.tsh_linked_at)}</p>
          ) : null}
        </CardBody>
      </Card>
    );
  }

  if (invoice.status !== "pending_tsh") {
    return null;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>TSH workflow</CardTitle>
        <Badge tone={apInvoiceTshWorkflowBadgeTone(workflowStatus)}>{workflowLabel}</Badge>
      </CardHeader>
      <CardBody className="space-y-4">
        {liveStatusMessage.trim() !== "" ? (
          <div aria-live="polite" className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-800">
            {liveStatusMessage}
          </div>
        ) : null}

        {invoice.tsh_workflow?.can_choose_parent ? (
          <ApInvoiceTshParentSelector invoiceId={invoiceId} invoice={invoice} onUpdated={onUpdated} />
        ) : null}

        {handoff?.parent_gipi != null ? (
          <div className="text-sm text-slate-700">
            <p className="font-medium text-slate-900">Parent GIPI</p>
            <p className="mt-1 inline-flex items-center gap-1">
              {handoff.parent_gipi.mv_number}
              <CopyToClipboardGlyph text={handoff.parent_gipi.mv_number} label="parent GIPI" />
              <MegaventoryPurchaseInvoiceExternalLink
                purchaseInvoiceMvId={handoff.parent_gipi.mv_id}
                mvNumber={handoff.parent_gipi.mv_number}
              />
            </p>
          </div>
        ) : invoice.tsh_parent_mv_number != null ? (
          <p className="inline-flex items-center gap-1 text-sm text-slate-700">
            Parent GIPI: <span className="font-medium">{invoice.tsh_parent_mv_number}</span>
            <CopyToClipboardGlyph text={invoice.tsh_parent_mv_number ?? ""} label="parent GIPI" />
          </p>
        ) : null}

        {handoff?.prior_month_tsh_message != null && handoff.prior_month_tsh_message.trim() !== "" ? (
          <p className="text-sm text-slate-700">{handoff.prior_month_tsh_message}</p>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Vendor invoice number</p>
            {megaventoryTshReference !== "" ? (
              <p className="inline-flex items-center gap-1 text-sm text-slate-900">
                <span className="font-mono">{megaventoryTshReference}</span>
                <CopyToClipboardGlyph text={megaventoryTshReference} label="vendor invoice number" />
              </p>
            ) : (
              <p className="text-sm text-rose-700">
                Missing — enter the vendor invoice number in Invoice details above, then save.
              </p>
            )}
          </div>
          <TshClipboardValue label="TSH total" display={totalDisplay} copyText={totalCopy} />
          <TshClipboardValue
            label="Tariff"
            display={formatApInvoiceTshAmountLabel(handoff?.tariff_amount ?? invoice.tsh_tariff_amount) || "—"}
            copyText={tariffCopy}
          />
          <TshClipboardValue
            label="Shipping"
            display={formatApInvoiceTshAmountLabel(handoff?.shipping_amount ?? invoice.tsh_shipping_amount) || "—"}
            copyText={shippingCopy}
          />
          <TshClipboardValue
            label="One-off fee"
            display={formatApInvoiceTshAmountLabel(handoff?.one_off_amount ?? invoice.tsh_one_off_amount) || "—"}
            copyText={oneOffCopy}
          />
          {oneOffDescription !== "" ? (
            <TshClipboardValue label="One-off fee description" display={oneOffDescription} copyText={oneOffDescription} />
          ) : null}
        </div>

        {canOpenMegaventoryBase &&
        workflowStatus !== "needs_link_review" &&
        handoff?.tsh_create_path !== "prior_month_gipi" ? (
          megaventoryTshReference !== "" ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
              <p className="font-medium">Before you save in Megaventory</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                <li>
                  Set <span className="font-medium">Document References</span> application reference to{" "}
                  <span className="inline-flex items-center gap-1 font-mono">
                    {megaventoryTshReference}
                    <CopyToClipboardGlyph text={megaventoryTshReference} label="Megaventory TSH reference" />
                  </span>{" "}
                  so the hub can match this TSH after ingest.
                </li>
                <li>Relate this TSH to the parent GIPI under Related Documents when Megaventory allows it.</li>
                <li>
                  Enter TSH lines totaling{" "}
                  <span className="inline-flex items-center gap-1 font-medium">
                    {totalDisplay}
                    <CopyToClipboardGlyph text={totalCopy} label="TSH total" />
                  </span>
                  .
                </li>
              </ul>
            </div>
          ) : (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-950">
              <p className="font-medium">Vendor invoice number required for TSH</p>
              <p className="mt-1">
                Enter the number from the vendor PDF in <span className="font-medium">Invoice details</span> above,
                save, then create the TSH in Megaventory using that same reference.
              </p>
            </div>
          )
        ) : null}

        {invoice.tsh_link_error?.trim() &&
        invoice.tsh_link_error.trim() !== "prior_month_gipi_creating" &&
        !invoice.tsh_link_error.trim().startsWith("prior_month_gipi_mv:") ? (
          <p role="alert" className="text-sm text-rose-700">
            {invoice.tsh_link_error.trim()}
          </p>
        ) : null}

        {workflowStatus === "needs_link_review" && reviewCandidates.length > 0 ? (
          <div className="space-y-3 border-t border-slate-200 pt-4">
            <p className="text-sm font-medium text-slate-900">Confirm ingested TSH document</p>
            <div className="space-y-2">
              {reviewCandidates.map((candidate) => {
                const candidateId = String(candidate.purchase_invoice_id);
                const selected = selectedCandidateId === candidateId;
                return (
                  <label
                    key={candidateId}
                    className={`flex cursor-pointer gap-3 rounded-md border px-3 py-2 text-sm ${
                      selected ? "border-brand-500 bg-brand-50" : "border-slate-200 bg-white"
                    }`}
                  >
                    <input
                      type="radio"
                      name="ap-tsh-link-candidate"
                      value={candidateId}
                      checked={selected}
                      onChange={() => setSelectedCandidateId(candidateId)}
                      className="mt-1"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="font-medium text-slate-900">
                        {candidate.mv_number ?? `TSH #${candidate.purchase_invoice_id}`}
                      </span>
                      <span className="mt-1 block text-slate-600">
                        {formatMoney(candidate.total_amount, candidate.currency ?? invoice.currency)}
                        {candidate.reference != null && candidate.reference.trim() !== ""
                          ? ` · ref ${candidate.reference.trim()}`
                          : ""}
                      </span>
                      <span className="mt-1 block text-xs text-slate-500">{candidateReviewLabel(candidate)}</span>
                    </span>
                  </label>
                );
              })}
            </div>
            <Button
              type="button"
              variant="secondary"
              disabled={selectedCandidateId === "" || linkMutation.isPending}
              onClick={() => {
                const parsed = Number.parseInt(selectedCandidateId, 10);
                if (Number.isFinite(parsed) && parsed > 0) {
                  linkMutation.mutate(parsed);
                }
              }}
            >
              {linkMutation.isPending ? "Linking…" : "Confirm TSH link"}
            </Button>
            {linkMutation.isError ? (
              <p role="alert" className="text-sm text-rose-700">
                {linkMutation.error instanceof Error ? linkMutation.error.message : "Could not link TSH."}
              </p>
            ) : null}
          </div>
        ) : null}

        {invoice.tsh_purchase_invoice_mv_number != null ? (
          <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
            Linked TSH: {invoice.tsh_purchase_invoice_mv_number}{" "}
            <QuickBooksBillTextLink
              txnId={invoice.tsh_purchase_invoice_qb_id}
              documentLabel={invoice.tsh_purchase_invoice_mv_number}
            />
            {invoice.tsh_linked_at != null ? ` · ${formatTimestamp(invoice.tsh_linked_at)}` : ""}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}
